import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/*
 * IconButton — DESIGN_SYSTEM.md §2.1
 * Square icon-only button used in topbars, toolbars, calendar nav.
 * - aria-label is required (TS-enforced).
 * - Optional `dot` shows a notification indicator (top-right).
 */

const iconButtonVariants = cva(
  [
    'inline-flex items-center justify-center relative shrink-0',
    'transition-colors disabled:opacity-50 disabled:pointer-events-none',
    'tap-highlight-transparent',
  ].join(' '),
  {
    variants: {
      variant: {
        default:
          'bg-surface text-soft border border-line rounded-md hover:border-brand-500 hover:bg-brand-100 hover:text-brand-700',
        subtle:
          'bg-transparent text-soft rounded-md hover:bg-surface-2 hover:text-ink',
        solid:
          'bg-brand-500 text-white rounded-md hover:bg-brand-700',
      },
      size: {
        sm: 'h-7 w-7',   // 28
        md: 'h-8 w-8',   // 32
        lg: 'h-9 w-9',   // 36
      },
    },
    defaultVariants: {
      variant: 'default',
      size:    'lg',
    },
  }
);

type DotColor = 'brand' | 'success' | 'warn' | 'danger';

const dotColorClass: Record<DotColor, string> = {
  brand:   'bg-brand-500',
  success: 'bg-success',
  warn:    'bg-warn',
  danger:  'bg-danger',
};

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>,
    VariantProps<typeof iconButtonVariants> {
  /** Visible icon node (Lucide icon component or SVG element). */
  icon: ReactNode;
  /** Required for screen readers — IconButton has no text. */
  'aria-label': string;
  /** When true, renders a small notification dot in the top-right. */
  dot?: boolean;
  dotColor?: DotColor;
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, variant, size, icon, dot, dotColor = 'danger', ...props }, ref) => {
    return (
      <button
        ref={ref}
        type="button"
        className={cn(iconButtonVariants({ variant, size }), className)}
        {...props}
      >
        <span className="inline-flex h-4 w-4 items-center justify-center [&_svg]:h-full [&_svg]:w-full">
          {icon}
        </span>
        {dot && (
          <span
            className={cn(
              'absolute top-0.5 right-0.5 h-2 w-2 rounded-pill ring-2 ring-surface',
              dotColorClass[dotColor],
            )}
            aria-hidden="true"
          />
        )}
      </button>
    );
  },
);

IconButton.displayName = 'IconButton';

export { IconButton, iconButtonVariants };
